import { useEffect, useRef, useState } from "react";
import { api, apiError, fmtDate } from "../lib/api";
import { Plus, Trash2, ChevronDown, ChevronUp, Save, Languages } from "lucide-react";
import { toast } from "sonner";

export default function TemplateEditorPage() {
    const [sites, setSites] = useState([]);
    const [siteId, setSiteId] = useState(null);
    const [tpl, setTpl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState({});
    // Client-side stable IDs for sections + tasks so React never confuses siblings
    // after a delete (index-as-key causes focus loss + wrong-value bleed).
    const nextLidRef = useRef(1);
    function _withLids(t) {
        if (!t || !Array.isArray(t.sections)) return t;
        t.sections.forEach((s) => {
            if (s._lid == null) s._lid = nextLidRef.current++;
            (s.tasks || []).forEach((tk) => { if (tk._lid == null) tk._lid = nextLidRef.current++; });
        });
        return t;
    }

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/sites");
            setSites(data);
            if (data.length && !siteId) setSiteId(data[0].id);
        })();
    }, [siteId]);

    useEffect(() => {
        if (!siteId) return;
        setLoading(true);
        (async () => {
            try {
                const { data } = await api.get(`/sites/${siteId}/template`);
                setTpl(_withLids(data));
                const o = {};
                for (const s of data.sections) o[s.code] = true;
                setOpen(o);
            } catch (e) {
                toast.error(apiError(e));
                setTpl(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [siteId]);

    function updateSection(idx, patch) {
        setTpl((t) => ({ ...t, sections: t.sections.map((s, i) => (i === idx ? { ...s, ...patch } : s)) }));
    }

    function updateTask(sIdx, tIdx, patch) {
        setTpl((t) => ({
            ...t,
            sections: t.sections.map((s, i) =>
                i === sIdx ? { ...s, tasks: s.tasks.map((task, j) => (j === tIdx ? { ...task, ...patch } : task)) } : s
            ),
        }));
    }

    function addTask(sIdx) {
        setTpl((t) => {
            const allNums = t.sections.flatMap((s) => s.tasks.map((x) => x.number));
            const nextNum = (allNums.length ? Math.max(...allNums) : 0) + 1;
            return {
                ...t,
                sections: t.sections.map((s, i) =>
                    i === sIdx ? { ...s, tasks: [...s.tasks, { _lid: nextLidRef.current++, number: nextNum, text: "New task", priority: "STANDARD", reference_note: null, text_hi: null, text_bn: null }] } : s
                ),
            };
        });
    }

    function removeTask(sIdx, tIdx) {
        setTpl((t) => ({
            ...t,
            sections: t.sections.map((s, i) => (i === sIdx ? { ...s, tasks: s.tasks.filter((_, j) => j !== tIdx) } : s)),
        }));
    }

    function addSection() {
        setTpl((t) => ({
            ...t,
            sections: [...t.sections, { _lid: nextLidRef.current++, code: String.fromCharCode(65 + t.sections.length), title: "NEW SECTION", tasks: [] }],
        }));
    }

    function removeSection(idx) {
        setTpl((t) => ({ ...t, sections: t.sections.filter((_, i) => i !== idx) }));
    }

    async function publish() {
        if (!tpl) return;
        setSaving(true);
        try {
            const payload = { sections: tpl.sections.map((s) => ({ code: s.code, title: s.title, tasks: s.tasks.map((t, i) => ({ number: t.number, text: t.text, priority: t.priority, reference_note: t.reference_note || null, text_hi: t.text_hi || null, text_bn: t.text_bn || null })) })) };
            const { data } = await api.post(`/sites/${siteId}/template/publish`, payload);
            toast.success(`Published v${data.version} — effective ${fmtDate(data.effective_from_date)}`);
            setTpl(_withLids(data));
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    }

    const isDraft = tpl?.status !== "published" || tpl?.is_draft_placeholder;

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">HO Admin</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">Template Editor</h1>
                    <p className="text-sm text-gray-500 mt-1">Edits publish as a new version, effective the next day. Today's open runs are unaffected.</p>
                </div>
                <div className="flex items-center gap-2">
                    <select
                        value={siteId || ""}
                        onChange={(e) => setSiteId(e.target.value)}
                        className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]"
                        data-testid="template-site-select"
                    >
                        {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button
                        onClick={publish}
                        disabled={!tpl || saving}
                        className="inline-flex items-center gap-1.5 bg-[#1E3355] hover:bg-[#15243C] text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50"
                        data-testid="template-publish-btn"
                    >
                        <Save className="w-4 h-4" /> {saving ? "Publishing…" : "Publish new version"}
                    </button>
                </div>
            </div>

            {tpl && (
                <div className="mt-4 flex items-center gap-2 text-xs">
                    <span className="text-gray-500">Current version</span>
                    <span className="font-semibold text-[#1E3355]">v{tpl.version}</span>
                    <span className="text-gray-400">·</span>
                    {isDraft ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-wider">Draft — HO to customise per client agreement</span>
                    ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 uppercase tracking-wider">Published · effective {fmtDate(tpl.effective_from_date)}</span>
                    )}
                </div>
            )}

            {loading || !tpl ? (
                <div className="mt-8 h-40 rounded-xl bg-white animate-pulse" />
            ) : (
                <div className="mt-6 space-y-4">
                    {tpl.sections.map((section, sIdx) => (
                        <div key={section._lid} className="rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                            <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
                                <div className="flex items-center gap-2 flex-1">
                                    <input
                                        value={section.code}
                                        onChange={(e) => updateSection(sIdx, { code: e.target.value })}
                                        className="w-14 rounded border border-gray-200 px-2 py-1 text-xs font-bold text-center"
                                        data-testid={`tpl-section-code-${sIdx}`}
                                    />
                                    <input
                                        value={section.title}
                                        onChange={(e) => updateSection(sIdx, { title: e.target.value })}
                                        className="flex-1 rounded border border-gray-200 px-2 py-1 text-sm font-semibold text-[#1E3355]"
                                        data-testid={`tpl-section-title-${sIdx}`}
                                    />
                                </div>
                                <div className="flex items-center gap-1 ml-2">
                                    <button onClick={() => setOpen((o) => ({ ...o, [section.code]: !(o[section.code] ?? true) }))} className="p-1.5 rounded hover:bg-gray-100" data-testid={`tpl-section-toggle-${sIdx}`}>
                                        {open[section.code] ?? true ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                                    </button>
                                    <button onClick={() => removeSection(sIdx)} className="p-1.5 rounded hover:bg-red-50 text-[#E02B2B]" data-testid={`tpl-section-remove-${sIdx}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                            {(open[section.code] ?? true) && (
                                <div className="divide-y divide-gray-100">
                                    {section.tasks.map((t, tIdx) => (
                                        <div key={t._lid} className="p-3 space-y-2" data-testid={`tpl-task-${sIdx}-${tIdx}`}>
                                            <div className="flex items-start gap-2">
                                                <input
                                                    type="number"
                                                    value={t.number}
                                                    onChange={(e) => updateTask(sIdx, tIdx, { number: Number(e.target.value) })}
                                                    className="w-14 rounded border border-gray-200 px-2 py-1.5 text-xs text-center"
                                                />
                                                <input
                                                    value={t.text}
                                                    onChange={(e) => updateTask(sIdx, tIdx, { text: e.target.value })}
                                                    className="flex-1 rounded border border-gray-200 px-2 py-1.5 text-sm"
                                                    placeholder="English"
                                                    data-testid={`tpl-task-text-${sIdx}-${tIdx}`}
                                                />
                                                <select
                                                    value={t.priority}
                                                    onChange={(e) => updateTask(sIdx, tIdx, { priority: e.target.value })}
                                                    className={`rounded border border-gray-200 px-2 py-1.5 text-xs font-semibold ${t.priority === "CRITICAL" ? "text-[#E02B2B]" : "text-gray-600"}`}
                                                    data-testid={`tpl-task-priority-${sIdx}-${tIdx}`}
                                                >
                                                    <option value="CRITICAL">CRITICAL</option>
                                                    <option value="STANDARD">STANDARD</option>
                                                </select>
                                                <button onClick={() => removeTask(sIdx, tIdx)} className="p-1.5 rounded hover:bg-red-50 text-[#E02B2B]" data-testid={`tpl-task-remove-${sIdx}-${tIdx}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                            <div className="pl-16 flex flex-col sm:flex-row gap-2">
                                                <div className="flex-1 flex items-center gap-1.5">
                                                    <Languages className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                                    <span className="text-[10px] font-semibold text-gray-500 w-8 shrink-0">HI</span>
                                                    <input
                                                        value={t.text_hi || ""}
                                                        onChange={(e) => updateTask(sIdx, tIdx, { text_hi: e.target.value })}
                                                        placeholder="हिन्दी (optional — falls back to English)"
                                                        className="flex-1 rounded border border-gray-200 px-2 py-1 text-xs"
                                                        data-testid={`tpl-task-text-hi-${sIdx}-${tIdx}`}
                                                    />
                                                </div>
                                                <div className="flex-1 flex items-center gap-1.5">
                                                    <span className="text-[10px] font-semibold text-gray-500 w-8 shrink-0 sm:pl-4">BN</span>
                                                    <input
                                                        value={t.text_bn || ""}
                                                        onChange={(e) => updateTask(sIdx, tIdx, { text_bn: e.target.value })}
                                                        placeholder="বাংলা (optional — falls back to English)"
                                                        className="flex-1 rounded border border-gray-200 px-2 py-1 text-xs"
                                                        data-testid={`tpl-task-text-bn-${sIdx}-${tIdx}`}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                    <button onClick={() => addTask(sIdx)} className="w-full py-2.5 text-xs font-medium text-[#1E3355] hover:bg-gray-50 flex items-center justify-center gap-1" data-testid={`tpl-add-task-${sIdx}`}>
                                        <Plus className="w-3.5 h-3.5" /> Add task
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    <button onClick={addSection} className="w-full py-3 rounded-xl border-2 border-dashed border-gray-200 text-sm font-medium text-gray-500 hover:border-[#1E3355] hover:text-[#1E3355] flex items-center justify-center gap-1.5" data-testid="tpl-add-section">
                        <Plus className="w-4 h-4" /> Add section
                    </button>
                </div>
            )}
        </div>
    );
}
